import { WELCOME_MOTION as T } from "./welcomeMotion"

/**
 * The welcome "bloom": a wide, soft D major chord of detuned saws swells with
 * the ember, its lowpass opens exactly on the flash (t0 + T.audioHitAt), a
 * sub adds weight and two small glass bells ring out on top. Every source is
 * scheduled on the click clock and stops by t0 + T.audioStop.
 */

// D major spread over two octaves: D3, A3, D4, F#4, A4.
const CHORD = [146.83, 220, 293.66, 369.99, 440]
const DETUNE_CENTS = [-7, 7]
const SUB = 73.42

const cents = (frequency: number, amount: number) =>
  frequency * 2 ** (amount / 1200)

/** A delay line with a filtered feedback tail, returned as its input node. */
const createSpace = (ctx: AudioContext, master: AudioNode) => {
  const delay = ctx.createDelay()
  delay.delayTime.value = 0.14
  const feedback = ctx.createGain()
  feedback.gain.value = 0.3
  const tail = ctx.createBiquadFilter()
  tail.type = "lowpass"
  tail.frequency.value = 3200
  const wet = ctx.createGain()
  wet.gain.value = 0.3
  delay.connect(feedback).connect(tail).connect(delay)
  delay.connect(wet).connect(master)
  return delay
}

/**
 * A glassy FM bell: the modulator's depth collapses quickly, which gives the
 * bright struck attack followed by a pure sine tail.
 */
const ringBell = (
  ctx: AudioContext,
  output: AudioNode,
  frequency: number,
  start: number,
  peak: number,
  release: number,
  stop: number,
) => {
  const carrier = ctx.createOscillator()
  carrier.type = "sine"
  carrier.frequency.value = frequency
  const modulator = ctx.createOscillator()
  modulator.type = "sine"
  modulator.frequency.value = frequency * 3.5
  const depth = ctx.createGain()
  depth.gain.setValueAtTime(frequency * 1.6, start)
  depth.gain.exponentialRampToValueAtTime(1, start + 0.35)
  modulator.connect(depth).connect(carrier.frequency)
  const amp = ctx.createGain()
  amp.gain.setValueAtTime(0, start)
  amp.gain.linearRampToValueAtTime(peak, start + 0.003)
  amp.gain.exponentialRampToValueAtTime(0.0001, start + release)
  carrier.connect(amp).connect(output)
  modulator.start(start)
  carrier.start(start)
  modulator.stop(stop)
  carrier.stop(stop)
}

export const renderWelcomeSound = (
  ctx: AudioContext,
  master: AudioNode,
  t0: number,
) => {
  const hit = t0 + T.audioHitAt
  const stop = t0 + T.audioStop

  const filter = ctx.createBiquadFilter()
  filter.type = "lowpass"
  filter.Q.value = 0.7
  filter.frequency.setValueAtTime(380, t0)
  filter.frequency.exponentialRampToValueAtTime(2600, hit)
  filter.frequency.exponentialRampToValueAtTime(900, hit + 1.1)
  const chordGain = ctx.createGain()
  chordGain.gain.setValueAtTime(0, t0)
  chordGain.gain.linearRampToValueAtTime(0.32, hit)
  chordGain.gain.linearRampToValueAtTime(0.26, hit + 0.2)
  chordGain.gain.exponentialRampToValueAtTime(0.0001, hit + 1.2)
  filter.connect(chordGain).connect(master)

  CHORD.forEach((frequency) => {
    DETUNE_CENTS.forEach((detune) => {
      const saw = ctx.createOscillator()
      saw.type = "sawtooth"
      saw.frequency.value = cents(frequency, detune)
      const level = ctx.createGain()
      level.gain.value = 0.12
      saw.connect(level).connect(filter)
      saw.start(t0)
      saw.stop(stop)
    })
  })

  const sub = ctx.createOscillator()
  sub.type = "sine"
  sub.frequency.value = SUB
  const subGain = ctx.createGain()
  subGain.gain.setValueAtTime(0, t0)
  subGain.gain.linearRampToValueAtTime(0.35, hit)
  subGain.gain.exponentialRampToValueAtTime(0.0001, hit + 1.1)
  sub.connect(subGain).connect(master)
  sub.start(t0)
  sub.stop(stop)

  const bellBus = ctx.createGain()
  bellBus.gain.value = 1
  bellBus.connect(master)
  bellBus.connect(createSpace(ctx, master))
  ringBell(ctx, bellBus, 587.33, hit, 0.3, 1.0, stop)
  ringBell(ctx, bellBus, 880, hit + 0.05, 0.18, 0.9, stop)
}
